import styled from '@emotion/styled'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useRecoilValue, useSetRecoilState } from 'recoil'

import { NodeType, SupportedLanguage } from '../../../__generated__/api'
import Loader from '../../../common/components/Loader/Loader'
import {
  APPOINTMENT_PARAMETERS,
  SEARCH_PARAMETERS,
  SESSION_STORAGE_KEYS,
} from '../../../common/constants/urlParameters'
import { useApi, useApi2 } from '../../../common/hooks/useApi'
import { useModals } from '../../../common/hooks/useModals'
import { useNode } from '../../../common/hooks/useNode'
import { useOHC } from '../../../common/hooks/useOHC'
import useTranslation from '../../../common/hooks/useTranslation'
import api from '../../../common/services/api'
import { ApiError } from '../../../common/utils/error/utils'
import { filterExternalUrlParameters } from '../../../common/utils/url'
import { flatten } from '../../../common/utils/utils'
import {
  generalErrorMessageAtom,
  isFromAppAtom,
  selectedAppointmentIdAtom,
  selectedAppointmentLengthAtom,
  selectedAppointmentNodeIdAtom,
} from '../../../state/common/atoms'
import { selectedAppointmentDetailsAtom } from '../../../state/reserve/atoms'
import { useSearchEvents } from '../../../xstate/events'
import { SearchTargetValue } from '../../../xstate/types'
import AppointmentNotFoundView from '../../appointmentNotFound/components/appointmentNotFoundView'
import {
  GetFlexibleWorklistLengthOptions,
  GetReqularWorkListOptions,
} from '../../appointmentOptionsSelect/LengthOptionsHelper'
import AppointmentOptionsSelectModal, {
  AppointmentOptions,
} from '../../appointmentOptionsSelect/components/AppointmentOptionsSelectModal'
import AppointmentNoteModal from '../../search/components/AppointmentNoteModal'
import { usePreselectionModals } from '../hooks/usePreselectionModals'

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
`

interface Props {
  basePath: string
}

const PreselectionView: React.FC<Props> = ({ basePath }) => {
  const history = useHistory()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const { isOHCSide } = useOHC()
  const isFromApp = useRecoilValue(isFromAppAtom)
  const setSelectedAppointmentId = useSetRecoilState(selectedAppointmentIdAtom)
  const setSelectedAppointmentNodeId = useSetRecoilState(selectedAppointmentNodeIdAtom)
  const setSelectedAppointmentDetails = useSetRecoilState(selectedAppointmentDetailsAtom)
  const setSelectedAppointmentLength = useSetRecoilState(selectedAppointmentLengthAtom)
  const setGeneralErrorMessage = useSetRecoilState(generalErrorMessageAtom)

  const { appointmentId, nodeId } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    return {
      appointmentId: searchParams.get('appointmentId'),
      nodeId: searchParams.get('nodeId'),
    }
  }, [location.search])

  const [notFound, setNotFound] = useState(false)

  const { setSearchTarget, setSearchLocation } = useSearchEvents()

  useEffect(() => {
    if (nodeId) {
      setSelectedAppointmentNodeId(nodeId)
      // Sync node with the XState search machine so that navigating back to the
      // search view shows results for this node. The XState machine parsed the URL
      // once on app startup (the preselect URL) which lacked the search node param.
      setSearchTarget({ id: nodeId, value: SearchTargetValue.Node })
    }
  }, [nodeId, setSelectedAppointmentNodeId, setSearchTarget])

  useEffect(() => {
    if (!appointmentId) {
      history.replace(basePath)
    }
  }, [appointmentId, basePath, history])

  useEffect(() => {
    // Preselect bypasses the search view's landing modal. Mark it as shown so the
    // XState guard doesn't trigger it when the user navigates back.
    sessionStorage.setItem(SESSION_STORAGE_KEYS.LANDING_MODAL_SHOWN, 'true')
  }, [])

  const { data: appointmentInfo, error: appointmentInfoError } = useApi2(
    api.v1.appointmentInfo,
    {
      appointmentId: appointmentId ?? '',
      lang: i18n.language as SupportedLanguage,
      nodeId: nodeId ?? undefined,
    },
    null,
    Boolean(appointmentId)
  )

  const buildBackPath = useCallback(() => {
    const params = filterExternalUrlParameters(location.search)
    if (nodeId) {
      params.set(SEARCH_PARAMETERS.NODE_ID, nodeId)
    }
    if (appointmentInfo?.searchLocationId) {
      params.set(`${SEARCH_PARAMETERS.LOCATION}[0]`, appointmentInfo.searchLocationId)
    }
    return `${basePath}?${params}`
  }, [basePath, location.search, nodeId, appointmentInfo?.searchLocationId])

  const { node, pending: nodePending } = useNode(nodeId ?? undefined)
  const { modals, pending: modalsPending } = useModals()

  const onDigitalClinic = useCallback(
    (queue: string) => {
      if (isFromApp) {
        window.location.replace(t('component.digitalClinicTile.redirectUrlOMApp'))
      } else {
        window.open(
          `${t(
            'component.digitalClinicTile.redirectUrlOMWeb'
          )}/?redirectAfterLogin=/digital-clinic/new/${encodeURIComponent(queue)}`
        )
      }
    },
    [isFromApp, t]
  )

  const resolvedAppointmentId = appointmentInfo?.id

  // Options modal is DH-only: AssisDent appointments skip duration selection (same as search flow).
  const numericAppointmentId =
    typeof resolvedAppointmentId === 'number' ? resolvedAppointmentId : undefined

  // Intentionally true when nodeId is absent: preselect links always show duration/restriction
  // info for DH appointments. The ServicePackage guard only applies when a node is provided.
  const shouldShowOptionsModal =
    numericAppointmentId != null && node?.type !== NodeType.ServicePackage

  const { data: practitionerDetails, error: practitionerDetailsError } = useApi(
    api.v1.getPractitionerDetails,
    {
      lang: i18n.language as SupportedLanguage,
      practitionerId: appointmentInfo?.practitionerId ?? 0,
      isOhc: isOHCSide,
    },
    null,
    Boolean(appointmentInfo) && shouldShowOptionsModal
  )

  const { data: appointmentLengthOptions, error: appointmentLengthOptionsError } = useApi(
    api.v1.getAppointmentLengthOptions,
    {
      lang: i18n.language as SupportedLanguage,
      appointmentId: numericAppointmentId ?? 0,
      nodeId: nodeId ?? undefined,
    },
    null,
    Boolean(numericAppointmentId) && shouldShowOptionsModal
  )

  const optionsModalDataFailed = Boolean(practitionerDetailsError || appointmentLengthOptionsError)

  const serviceTerms = useMemo(
    () =>
      flatten(
        node?.connections?.map((connection) =>
          connection.terms.filter((term) => term.type === 'service').map((term) => term.term)
        ) ?? []
      ),
    [node?.connections]
  )

  const lengthOptions = useMemo(() => {
    if (!practitionerDetails || !appointmentLengthOptions || !appointmentInfo) return null
    if (practitionerDetails.hasFlexibleSchedule) {
      return GetFlexibleWorklistLengthOptions(
        practitionerDetails.flexibleScheduleOptions,
        serviceTerms,
        appointmentLengthOptions,
        []
      )
    }
    return GetReqularWorkListOptions(
      practitionerDetails.appointmentLengthOptions,
      appointmentLengthOptions,
      appointmentInfo.duration
    )
  }, [practitionerDetails, appointmentLengthOptions, appointmentInfo, serviceTerms])

  const setAppointmentDetails = useCallback(() => {
    if (!appointmentInfo) return
    setSelectedAppointmentId(resolvedAppointmentId)
    setSelectedAppointmentDetails({
      time: appointmentInfo.date,
      duration: appointmentInfo.duration,
      location: appointmentInfo.locationId,
      locationName: appointmentInfo.locationName,
      locationAddress: appointmentInfo.locationAddress,
      specialistId: appointmentInfo.practitionerId,
      specialistName: appointmentInfo.practitionerName,
      specialistTitle: appointmentInfo.practitionerTitle,
      isResourceList: appointmentInfo.isResourceList,
      image: appointmentInfo.practitionerImage,
      appointmentTypes: [],
      appointmentId: numericAppointmentId,
      hasFlexibleScheduleOptions: appointmentInfo.practitionerHasFlexibleSchedule,
    })
  }, [
    appointmentInfo,
    resolvedAppointmentId,
    numericAppointmentId,
    setSelectedAppointmentId,
    setSelectedAppointmentDetails,
  ])

  const navigateToAppointment = useCallback(
    (options?: AppointmentOptions) => {
      if (!appointmentInfo) return

      setAppointmentDetails()

      const params = new URLSearchParams()
      if (nodeId) {
        params.append(APPOINTMENT_PARAMETERS.NODE, nodeId)
      }
      if (options) {
        if (options.duration === 'default' || typeof options.duration === 'number') {
          params.append(APPOINTMENT_PARAMETERS.LENGTH, String(options.duration))
          setSelectedAppointmentLength(options.duration)
        }
        if (options.serviceId != null) {
          params.append(APPOINTMENT_PARAMETERS.SERVICE, String(options.serviceId))
        }
      } else {
        params.append(APPOINTMENT_PARAMETERS.LENGTH, String(appointmentInfo.duration))
      }

      if (appointmentInfo.searchLocationId) {
        setSearchLocation([appointmentInfo.searchLocationId])
      }

      const appointmentUrl = `${basePath}/appointment/${appointmentInfo.id}${
        params.toString() ? `?${params}` : ''
      }`
      // Replace the transient /preselect entry with the search page, then push the
      // appointment view on top. This ensures history.goBack() returns to search.
      history.replace(buildBackPath())
      history.push(appointmentUrl)
    },
    [
      appointmentInfo,
      basePath,
      buildBackPath,
      history,
      nodeId,
      setAppointmentDetails,
      setSelectedAppointmentLength,
      setSearchLocation,
    ]
  )

  const {
    modalPhase,
    noteModalConfig,
    handleNoteModalContinue,
    handleNoteModalClose,
    handleOptionsSelect,
    handleOptionsClose,
  } = usePreselectionModals({
    appointmentInfo,
    node,
    nodePending,
    nodeId,
    isOhc: isOHCSide,
    modals,
    modalsLoaded: !modalsPending,
    shouldShowOptionsModal,
    lengthOptions,
    practitionerDetailsReady: Boolean(practitionerDetails),
    optionsModalDataFailed,
    onDigitalClinic,
    navigateToAppointment,
    buildBackPath,
  })

  useEffect(() => {
    if (!appointmentInfoError) return

    if ((appointmentInfoError as ApiError).statusCode === 404) {
      setNotFound(true)
      return
    }

    setGeneralErrorMessage('common.error')
    history.replace(buildBackPath())
  }, [appointmentInfoError, buildBackPath, history, setGeneralErrorMessage])

  if (notFound) {
    return (
      <div data-cy="preselection-not-found">
        <AppointmentNotFoundView
          onBackClick={() => {
            history.replace(buildBackPath())
          }}
        />
      </div>
    )
  }

  return (
    <>
      <LoaderContainer data-cy="preselection-loader">
        <Loader />
      </LoaderContainer>

      {modalPhase === 'noteModal' && noteModalConfig && (
        <div data-cy="preselection-note-modal">
          <AppointmentNoteModal
            note={{
              ...noteModalConfig,
              onContinue: handleNoteModalContinue,
              onClose: handleNoteModalClose,
            }}
          />
        </div>
      )}

      {modalPhase === 'optionsModal' &&
        practitionerDetails &&
        lengthOptions &&
        numericAppointmentId != null && (
          <div data-cy="preselection-options-modal">
            <AppointmentOptionsSelectModal
              appointmentId={numericAppointmentId}
              practitionerDetails={practitionerDetails}
              lengthOptions={lengthOptions}
              onSelect={handleOptionsSelect}
              onSelectUnavailableDuration={() => {
                history.replace(buildBackPath())
              }}
              onClose={handleOptionsClose}
            />
          </div>
        )}
    </>
  )
}

export default PreselectionView
